import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import { useAuth } from '../../contexts/AuthContext';
import { getDataPackages } from '../../services/dataPackagesService';
import NewsFeedSection from './NewsFeedSection';
import { apiGet } from '../../services/apiClient';
import AccessDeniedModal from './AccessDeniedModal';
import { AiAssistantAccessInfo, isQuotaLimitedAiMode } from '../../services/aiAssistantAccessService';

type DashboardSection = 'data' | 'employees' | 'ai-assistant';

type DashboardProps = {
  onNavigate?: (section: DashboardSection) => void;
  aiAccessInfo?: AiAssistantAccessInfo;
  aiAssistantAccessLoading?: boolean;
  aiHasConversation?: boolean;
};

const Dashboard: React.FC<DashboardProps> = ({
  onNavigate,
  aiAccessInfo,
  aiAssistantAccessLoading = false,
  aiHasConversation = false,
}) => {
  const hasAiAssistantAccess = Boolean(aiAccessInfo?.hasAccess);
  const canOpenAiChat = hasAiAssistantAccess || aiHasConversation;
  const isTrialMode = aiAccessInfo?.mode === 'trial';
  const isSubscriptionLimitedMode = aiAccessInfo?.mode === 'limited';
  const isQuotaLimitedMode = isQuotaLimitedAiMode(aiAccessInfo?.mode);
  const [aiContactModalOpen, setAiContactModalOpen] = useState(false);
  const [activeProjectsCount, setActiveProjectsCount] = useState<number>(0);
  const [orgCount, setOrgCount] = useState<number>(0);
  const [dataPackagesCount, setDataPackagesCount] = useState<number>(0);
  const { user } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) {
        setActiveProjectsCount(0);
        setOrgCount(0);
        setDataPackagesCount(0);
        return;
      }
      const stats = await apiGet<{ activeProjectsCount: number; orgCount: number }>('/dashboard/stats');
      setActiveProjectsCount(stats?.activeProjectsCount || 0);
      setOrgCount(stats?.orgCount || 0);
      
      // Считаем доступные пакеты данных
      try {
        const dataPackages = await getDataPackages();
        setDataPackagesCount(dataPackages.filter((pkg) => pkg.hasAccess === true).length);
      } catch (error) {
        console.error('Error fetching data packages count:', error);
        setDataPackagesCount(0);
      }
    };
    fetchStats();
  }, [user]);

  return (
    <div className="dashboard">
      <div className="stats-section">
        <div className="stats-section-header">
          <i className="fas fa-chart-bar"></i>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-folder"></i>
            </div>
            <div className="stat-content">
              <div className="stat-number">{activeProjectsCount}</div>
              <div className="stat-label">Активных проектов</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="stat-content">
              <div className="stat-number">0</div>
              <div className="stat-label">Отчетов создано</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-database"></i>
            </div>
            <div className="stat-content">
              <div className="stat-number">{dataPackagesCount}</div>
              <div className="stat-label">Наборов данных</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <div className="stat-number">{orgCount}</div>
              <div className="stat-label">Организаций</div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-tools-grid">
        <article className="dashboard-tool-card">
          <div className="dashboard-tool-icon">
            <i className="fas fa-database" aria-hidden="true"></i>
          </div>
          <h3 className="dashboard-tool-title">Обзор рынка</h3>
          <p className="dashboard-tool-text">Изучайте аналитику по доступным пакетам данным</p>
          <button type="button" className="dashboard-tool-btn" onClick={() => onNavigate?.('data')}>
            Перейти к обзору рынка <i className="fas fa-arrow-right" aria-hidden="true"></i>
          </button>
        </article>

        <article className="dashboard-tool-card">
          <div className="dashboard-tool-icon">
            <i className="fas fa-users" aria-hidden="true"></i>
          </div>
          <h3 className="dashboard-tool-title">Мои сотрудники</h3>
          <p className="dashboard-tool-text">Добавляйте сотрудников и сравнивайте их с рынком.</p>
          <button type="button" className="dashboard-tool-btn" onClick={() => onNavigate?.('employees')}>
            Перейти к сотрудникам <i className="fas fa-arrow-right" aria-hidden="true"></i>
          </button>
        </article>

        <article className={`dashboard-tool-card${canOpenAiChat ? '' : ' dashboard-tool-card--locked'}`}>
          <div className="dashboard-tool-icon">
            <i className={`fas ${canOpenAiChat ? 'fa-robot' : 'fa-lock'}`} aria-hidden="true"></i>
          </div>
          <h3 className="dashboard-tool-title">AI-ассистент</h3>
          <p className="dashboard-tool-text">
            {hasAiAssistantAccess && isSubscriptionLimitedMode
              ? `По подписке: осталось ${aiAccessInfo?.trialRemaining ?? 0} из ${aiAccessInfo?.trialLimit ?? 0} запросов к AI-ассистенту.`
              : hasAiAssistantAccess && isTrialMode
              ? `Пробный режим: осталось ${aiAccessInfo?.trialRemaining ?? 0} из ${aiAccessInfo?.trialLimit ?? 3} запросов к AI-ассистенту.`
              : hasAiAssistantAccess
                ? 'Задавайте вопросы по рыночным данным и компенсациям — ответы на базе вашей подписки.'
                : aiHasConversation
                  ? 'Пробные запросы исчерпаны. История чата сохранена — новые сообщения недоступны.'
                  : aiAccessInfo?.trialUsed && aiAccessInfo.trialUsed >= (aiAccessInfo.trialLimit || 3)
                    ? 'Пробные запросы исчерпаны. Запросите презентацию, чтобы подключить полный доступ.'
                    : 'Доступен по подписке datacircle. Запросите презентацию, чтобы подключить ассистента.'}
          </p>
          <button
            type="button"
            className="dashboard-tool-btn"
            disabled={aiAssistantAccessLoading}
            onClick={() => {
              if (canOpenAiChat) {
                onNavigate?.('ai-assistant');
              } else {
                setAiContactModalOpen(true);
              }
            }}
          >
            {hasAiAssistantAccess
              ? (
                isQuotaLimitedMode
                  ? <>Открыть AI-ассистент <i className="fas fa-arrow-right" aria-hidden="true"></i></>
                  : <>Перейти к AI-ассистенту <i className="fas fa-arrow-right" aria-hidden="true"></i></>
              )
              : aiHasConversation
                ? <>Открыть историю чата <i className="fas fa-arrow-right" aria-hidden="true"></i></>
                : <>Запросить презентацию <i className="fas fa-envelope" aria-hidden="true"></i></>}
          </button>
        </article>
      </div>

      <AccessDeniedModal
        isOpen={aiContactModalOpen}
        onClose={() => setAiContactModalOpen(false)}
        packageName="AI-ассистент"
        context="AI-ассистент"
      />

      {/* Лента новостей с дашбордами */}
      <NewsFeedSection />
    </div>
  );
};

export default Dashboard; 